import { api } from "@/api/client";

// Tesla RMA remits (app/services/tesla_rma): one upload of a Tesla portal
// remittance export = one bank deposit.

export type TrRecordType = "sales_order" | "invoice" | "payment";

export interface TrRecord {
  type: TrRecordType;
  key: string;
  external_id: string;
  amount: string;
  location_id?: number | null;
  memo?: string;
  tranid?: string;
  invoice_id?: number;
  sales_order_id?: number;
}

export interface TrFlag {
  code: string;
  message: string;
  blocking: boolean;
}

export interface TrLine {
  line: number;
  invoice_number: string;
  ref: string | null;
  short_ref: string | null;
  last_name: string | null;
  billed: string | null;
  paid: string;
  records: TrRecord[];
  flags: TrFlag[];
  blocked: boolean;
  already_booked: boolean;
  booked_by_hand: boolean;
  creates_order?: boolean;
  short_amount?: string;
  location_id?: number | null;
  sales_order?: { id: number; tranid: string; total: string };
  invoice?: { id: number; tranid: string; total: string; unpaid: string };
  booked_payment?: { id: number; tranid: string; trandate: string; amount: string };
}

export interface TrPlan {
  reference?: string;
  expected_total?: string;
  deposit_total?: string;
  booked_elsewhere_total?: string;
  short_total?: string;
  reconciled?: boolean;
  already_booked?: boolean;
  blocked?: boolean;
  counts?: { lines: number; to_post: number; new_orders: number; already_booked: number; short_pays: number; blocked: number };
  lines?: TrLine[];
}

export type TrStatus = "planning" | "planned" | "posting" | "partly_posted" | "posted" | "already_booked" | "failed";

export interface TrLineState {
  status?: "posting" | "posted" | "failed" | null;
  error?: string | null;
  posted_at?: string;
  posted_by_id?: number;
}

export interface TeslaRemit {
  id: number;
  reference: string;
  filename: string;
  row_count: number;
  payment_on: string | null;
  deposit_on: string | null;
  expected_total: string;
  status: TrStatus;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  // Detail only.
  netsuite_base_url?: string | null;
  write_enabled?: boolean;
  postable?: Record<string, boolean>;
  line_states?: Record<string, TrLineState>;
  deposit_ready?: boolean;
  date_locked?: boolean;
  posted_elsewhere?: boolean;
  posted_at?: string | null;
  posted_by?: string | null;
  netsuite_deposit_id?: number | null;
  posted?: Record<string, number>;
  locations?: Record<string, string>;
  plan?: TrPlan;
}

export interface LineFix {
  line: string;
  project_ref?: string;
  location_id?: number | null;
  booked_by_hand?: boolean;
}

export async function fetchTeslaRemits(): Promise<TeslaRemit[]> {
  const { data } = await api.get<TeslaRemit[]>("/tesla_remits");
  return data;
}

export async function fetchTeslaRemit(id: number): Promise<TeslaRemit> {
  const { data } = await api.get<TeslaRemit>(`/tesla_remits/${id}`);
  return data;
}

export async function uploadTeslaRemit(file: File, reference: string, depositOn: string): Promise<TeslaRemit> {
  const form = new FormData();
  form.append("file", file);
  form.append("reference", reference);
  if (depositOn) form.append("deposit_on", depositOn);
  const { data } = await api.post<TeslaRemit>("/tesla_remits", form);
  return data;
}

export async function updateTeslaRemit(id: number, input: { deposit_on?: string; line?: LineFix }): Promise<TeslaRemit> {
  const { data } = await api.patch<TeslaRemit>(`/tesla_remits/${id}`, input);
  return data;
}

export async function replanTeslaRemit(id: number): Promise<TeslaRemit> {
  const { data } = await api.post<TeslaRemit>(`/tesla_remits/${id}/replan`);
  return data;
}

export async function postTeslaLine(id: number, line: number): Promise<TeslaRemit> {
  const { data } = await api.post<TeslaRemit>(`/tesla_remits/${id}/post_line`, { line: String(line) });
  return data;
}

export async function postTeslaDeposit(id: number): Promise<TeslaRemit> {
  const { data } = await api.post<TeslaRemit>(`/tesla_remits/${id}/post_deposit`);
  return data;
}
